import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { notification } from 'antd';
import { groupBy, map, sumBy } from 'lodash';

import { fetch as axios } from '../../axios_config';

import { handleRejectedResponse } from './utils';

export const GetPlacementOptions = createAsyncThunk(
  'inbound-shipment/list-placement-options',
  async (workOrderId, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/inbound-shipment/list-placement-options?workOrderId=${workOrderId}`,
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'List Placement Options', rejectWithValue);
    }
  }
);

export const GetShipmentItems = createAsyncThunk(
  'inbound-shipment/list-shipment-items',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/inbound-shipment/list-shipment-items`,
        data
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'List Shipment Items', rejectWithValue);
    }
  }
);

export const GetShipmentDetails = createAsyncThunk(
  'inbound-shipment/user-shipments',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/inbound-shipment/user-shipments`,
        {
          params: data
        }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get Shipment Details', rejectWithValue);
    }
  }
);

export const CancelInboundPlan = createAsyncThunk(
  'inbound-shipment/cancel-inbound-plan',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/inbound-shipment/cancel-inbound-plan`,
        data
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Cancel Inbound Plan', rejectWithValue);
    }
  }
);

export const ConfirmPlacementOption = createAsyncThunk(
  'inbound-shipment/confirm-placement-option',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/inbound-shipment/confirm-placement-option`,
        data
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Confirm Placement Option', rejectWithValue);
    }
  }
);

const shipmentPlan = createSlice({
  name: 'shipmentPlan',
  initialState: {
    fetchingShipmentDetails: false,
    shipments: [],
    shipmentInfo: {},
    workOrder: {},
    workOrderItems: [],
    addressInfo: {},

    confirmingPlacementOption: false,
    cancelingInboundPlan: false,

    placementOptions: [],
    fetchingPlacementOptions: false,

    shipmentItems: [],
    fetchingShipmentItems: false,
  },
  reducers: {
    updateState(state, { payload: { key, value } }) {
      state[key] = value;
    }
  },
  extraReducers: {
    [GetPlacementOptions.pending]: (state) => {
      state.fetchingPlacementOptions = true;
      state.placementOptions = [];
    },
    [GetPlacementOptions.fulfilled]: (state, action) => {
      const { placementOptions } = action?.payload || {};

      state.placementOptions = placementOptions;
      state.fetchingPlacementOptions = false;
    },
    [GetPlacementOptions.rejected]: (state) => {
      state.fetchingPlacementOptions = false;
    },

    [GetShipmentItems.pending]: (state) => {
      state.fetchingShipmentItems = true;
      state.inboundPlans = [];
    },
    [GetShipmentItems.fulfilled]: (state, action) => {
      const { placementOptionsData } = action?.payload || {};

      state.inboundPlans = placementOptionsData;
      state.fetchingShipmentItems = false;
    },
    [GetShipmentItems.rejected]: (state) => {
      state.fetchingShipmentItems = false;
    },

    [GetShipmentDetails.pending]: (state) => {
      state.fetchingShipmentDetails = true;
    },
    [GetShipmentDetails.fulfilled]: (state, action) => {
      const { shipmentInfo } = action?.payload || {};
      const { shipments, workOrder, addressInfo = {} } = shipmentInfo;

      const workOrderItems = shipments?.flatMap(shipment => shipment?.shipment_items_info || []);

      const mergedData = map(groupBy(workOrderItems, "msku"), (group) => ({
        ...group[0],
        quantity: sumBy(group, (item) => Number(item.quantity)),
        boxed_quantity: sumBy(group, (item) => Number(item.boxed_quantity || 0)),
      }));

      state.workOrderItems = mergedData;
      state.shipments = shipments;
      state.workOrder = workOrder;
      state.addressInfo = addressInfo;
      state.fetchingShipmentDetails = false;
    },
    [GetShipmentDetails.rejected]: (state) => {
      state.fetchingShipmentDetails = false;
    },

    [CancelInboundPlan.pending]: (state) => {
      state.cancelingInboundPlan = true;
    },
    [CancelInboundPlan.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.cancelingInboundPlan = false;
      notification[success ? 'success' : 'error']({
        message,
        top: 65,
      });
    },
    [CancelInboundPlan.rejected]: (state) => {
      state.cancelingInboundPlan = false;
    },

    [ConfirmPlacementOption.pending]: (state) => {
      state.confirmingPlacementOption = true;
    },
    [ConfirmPlacementOption.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.confirmingPlacementOption = false;
      notification[success ? 'success' : 'error']({
        message,
        top: 65,
      });
    },
    [ConfirmPlacementOption.rejected]: (state) => {
      state.confirmingPlacementOption = false;
    },
  },
});

const { reducer, actions } = shipmentPlan;

export const { updateState } = actions;

export default reducer;
